'use client';

// @TODO: Refactor this page. It's a mess.
import './sac-controls.scss';

import { Suspense, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { throttle } from 'throttle-debounce';

import { ButtonStyle } from '@sola/ark-core/atoms/button-style/button-style.js';
import { Link } from '@sola/ark-core/atoms/link/link.js';
import { ChipGroup } from '@sola/ark-core/molecules/chip-group/chip-group.js';
import { RadioGroup } from '@sola/ark-core/molecules/radio-group/radio-group.js';
import { Select } from '@sola/ark-core/molecules/select/select.js';
import { SliderInput } from '@sola/ark-core/molecules/slider-input/slider-input.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.js';
import { generalConfig } from '@sola/config/lib/getters.js';
import {
  countryIsoToRegionIso,
  programCountryIsoCodes,
} from '@sola/contentful-model/lib/sac-values.js';
import { ChildGenderEnum } from '@sola/schema/dotcom/children/childUtilityInterfaces.js';
import { IconNames } from '@sola/schema/lib/icons/icon-names.js';
import { SAC_URL_PARAMS, SacFilterProps } from '@sola/schema/lib/schema.js';
import {
  TrackChildSearch,
  trackClick,
} from '@sola/tracking/components/datalayer.js';

import {
  SacFiltersEnum,
  SacQueryParams,
  useSacQueryParams,
} from '../../utils/query-params';
import { getBaseSizeProps } from '../../utils/text-utils';
import { FeatureBase } from '../feature-base/feature-base';

const { sacMinAge, sacMaxAge } = generalConfig;

type SacControlsProps = SacFilterProps & {
  variant?: 'default' | 'stacked';
  isLive?: boolean;
};

export const generateMonthItems = () =>
  [...Array(12).keys()]
    .map((v) => v + 1)
    .map((v) => {
      return {
        value: v.toString(),
        // TODO: es
        text: Intl.DateTimeFormat('en', { month: 'long' }).format(
          new Date(2000, v - 1),
        ),
      };
    });

export const generateDayItems = () =>
  [...Array(31).keys()]
    .map((v) => v + 1)
    .map((v) => {
      return {
        value: v.toString(),
        text: v.toString(),
      };
    });

const InternalSacControls: React.FC<
  SacControlsProps & {
    sacFilters?: SacQueryParams;
    setSacFilters?: (values: Partial<SacQueryParams>) => void;
  }
> = ({ variant = 'default', isLive, sacFilters, setSacFilters, ...props }) => {
  const { t } = useTranslation();

  const chips = [
    {
      name: t('skippy.sac.all'),
      value: '',
      isClear: true,
    },
    {
      name: t('skippy.sac.orphanedChild'),
      value: SAC_URL_PARAMS.ORPHANED_CHILD,
      icon: 'child' as IconNames,
    },
    {
      name: t('skippy.sac.hasDisabilities'),
      value: SAC_URL_PARAMS.HAS_DISABILITIES,
      icon: 'wheelchair' as IconNames,
    },
    {
      name: t('skippy.sac.vulnerableToExploitation'),
      value: SAC_URL_PARAMS.VULNERABLE_EXPLOITATION,
      icon: 'alert-triangle' as IconNames,
    },
    {
      name: t('skippy.sac.affectedByHiv'),
      value: SAC_URL_PARAMS.AFFECTED_HIV,
      icon: 'virus' as IconNames,
    },
  ];

  const [showAdvanced, setShowAdvanced] = useState<boolean>(
    sacFilters?.gender !== ChildGenderEnum.Either ||
      sacFilters?.ageMin !== undefined ||
      sacFilters?.ageMax !== undefined ||
      (sacFilters?.countries?.length ?? 0) > 0,
  );

  const [ageRange, setAgeRange] = useState<[number, number]>([
    sacFilters?.ageMin ?? sacMinAge,
    sacFilters?.ageMax ?? sacMaxAge,
  ]);

  const [isMultipleCountry, setIsMultipleCountry] = useState(
    sacFilters?.countries && sacFilters?.countries?.length > 1,
  );

  useEffect(() => {
    // our slider component is not a controlled component, so if we reset these values by clearing
    // the url params, we need to reset the age range to the defaults
    const minParam = sacFilters?.ageMin;
    const maxParam = sacFilters?.ageMax;

    if (minParam === undefined && maxParam === undefined) {
      setAgeRange([sacMinAge, sacMaxAge]);
    }
  }, [sacFilters?.ageMin, sacFilters?.ageMax]);

  const getGenderOptions = () => [
    {
      value: ChildGenderEnum.Male,
      text: t('skippy.sac.controls.boy'),
    },
    {
      value: ChildGenderEnum.Female,
      text: t('skippy.sac.controls.girl'),
    },
    { value: ChildGenderEnum.Either, text: t('skippy.sac.controls.either') },
  ];

  const getCountries = () => [
    { value: '', text: t('skippy.sac.controls.allCountries') },
    ...(isMultipleCountry
      ? [
          {
            value: 'multiple',
            text: t('skippy.sac.controls.multipleCountries'),
            disabled: true,
          },
        ]
      : []),
    ...programCountryIsoCodes
      .map((c) => {
        return {
          value: c,
          text: t(`geo.countries.${c}.nameS` as 'geo.countries.BD.nameS'),
        };
      })
      .sort((a, b) => a.value.localeCompare(b.value)),
  ];

  const getMonths = () => [
    { value: '0', text: t('skippy.sac.controls.month') },
    ...generateMonthItems(),
  ];

  const getDays = () => [
    { value: '0', text: t('skippy.sac.controls.day') },
    ...generateDayItems(),
  ];

  const throttledSliderHandler = throttle(
    300,
    (minAge: number, maxAge: number) => {
      setSacFilters?.({
        ageMin: minAge,
        ageMax: maxAge,
      });
    },
    { noLeading: true },
  );

  const getSacControls = (className?: string) => (
    <div
      className={`SacControls__controls-container p-comfy-xl grid grid-cols-1 md:grid-cols-3 gap-comfy-lg ${className}`}
    >
      <span>
        {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
        <label id="gender-group-label" className="form-label label">
          {t('skippy.sac.wantToSponsor')}
        </label>
        <RadioGroup
          defaultValue={sacFilters?.gender}
          aria-label={t('skippy.sac.controls.chooseGender')}
          loop={false}
          variant="buttons"
          name="gender"
          aria-labelledby="gender-group-label"
          onChange={(v) => {
            setSacFilters?.({ gender: v?.target?.value });
          }}
          items={getGenderOptions()}
        />
      </span>
      <span>
        <SliderInput
          value={ageRange}
          label={t('skippy.sac.betweenTheAges')}
          min={sacMinAge}
          max={sacMaxAge}
          name="age"
          showThumbLabels
          onChange={({ newValue }) => {
            const [minAgeValue, maxAgeValue] = newValue;
            setAgeRange([minAgeValue, maxAgeValue]);
          }}
          onCommit={({ newValue }) => {
            if (newValue?.length === 2) {
              const [minAgeValue, maxAgeValue] = newValue;
              setAgeRange([minAgeValue, maxAgeValue]);
              throttledSliderHandler(minAgeValue, maxAgeValue);
            }
          }}
        />
      </span>
      <span>
        <Select
          value={isMultipleCountry ? 'multiple' : sacFilters?.countries}
          label={t('skippy.sac.whoLivesIn')}
          name="country"
          onChange={(v) => {
            setIsMultipleCountry(false);
            setSacFilters?.({
              countries: [v?.target?.value].filter(Boolean),
            });
          }}
          items={getCountries()}
        />
      </span>
    </div>
  );
  // TODO: Add a button to reset the date
  const getBirthdayControls = () => (
    <div className="flex flex-col md:flex-row items-center justify-center p-comfy-lg gap-comfy-sm">
      <ArkIcon
        iconName="gift-01"
        className="SacControls__birthday-icon hidden md:block"
      />
      <div className="label-md label-moderate">
        {t('skippy.sac.birthdaySearch')}
      </div>
      <span className="flex gap-comfy-sm">
        <Select
          value={sacFilters?.birthMonth}
          name="birth month"
          aria-label={t('skippy.sac.controls.month')}
          onChange={(v) => {
            setSacFilters?.({
              birthMonth: Number(v?.target?.value),
            });
          }}
          items={getMonths()}
        />
        <Select
          value={sacFilters?.birthDay}
          name="birth day"
          aria-label={t('skippy.sac.controls.day')}
          onChange={(v) => {
            setSacFilters?.({
              birthDay: Number(v?.target?.value),
            });
          }}
          items={getDays()}
        />
      </span>
    </div>
  );

  const getAdvancedOptions = () => (
    <div className="SacControls__adv-search-container block md:hidden flex flex-col p-comfy-xs md:p-comfy-sm justify-center">
      {showAdvanced && getSacControls()}
      <ButtonStyle
        variant="tertiary"
        fullWidth
        iconAfter={
          showAdvanced ? (
            <ArkIcon iconName="chevron-up" />
          ) : (
            <ArkIcon iconName="chevron-down" />
          )
        }
      >
        <button
          type="button"
          onClick={() => {
            trackClick(t('skippy.sac.advancedSearch'));
            setShowAdvanced((v) => !v);
          }}
        >
          {t('skippy.sac.advancedSearch')}
        </button>
      </ButtonStyle>
    </div>
  );

  const showChips = variant !== 'stacked';

  return (
    // TODO: this layout isn't exactly right as the spacing is a bit off
    <div
      className={cn(
        `SacControls SacControls--${variant} flex flex-col gap-roomy-xs`,
      )}
      aria-label={t('skippy.sac.controls.legend')}
      role="group"
    >
      <TrackChildSearch
        event={{
          gender: sacFilters?.gender || undefined,
          ageMin: sacFilters?.ageMin || undefined,
          ageMax: sacFilters?.ageMax || undefined,
          region: countryIsoToRegionIso(sacFilters?.countries?.[0] || ''),
          country: Array.from(sacFilters?.countries || []),
          birthMonth: sacFilters?.birthMonth || undefined,
          birthDay: sacFilters?.birthDay || undefined,
          orphan:
            sacFilters?.filters?.some(
              (f: string) => f === SAC_URL_PARAMS.ORPHANED_CHILD,
            ) || undefined,
          disability:
            sacFilters?.filters?.some(
              (f: string) => f === SAC_URL_PARAMS.HAS_DISABILITIES,
            ) || undefined,
          highlyVulnerable:
            sacFilters?.filters?.some(
              (f: string) => f === SAC_URL_PARAMS.VULNERABLE_EXPLOITATION,
            ) || undefined,
          hiv:
            sacFilters?.filters?.some(
              (f: string) => f === SAC_URL_PARAMS.AFFECTED_HIV,
            ) || undefined,
        }}
      />
      <div className="SacControls__card flex flex-col">
        {variant !== 'stacked' && (
          <>
            {getSacControls('hidden md:grid')}
            {getBirthdayControls()}
            {getAdvancedOptions()}
          </>
        )}
        {variant === 'stacked' && (
          <>
            <FeatureBase
              {...props}
              {...getBaseSizeProps(2)}
              {...{
                primaryCTA: undefined,
                secondaryCTA: undefined,
                tertiaryCTA: undefined,
              }}
              className="p-comfy-xl pb-none"
              inline
            />
            {getSacControls('flex flex-col')}
          </>
        )}
        {!isLive && (
          <div className="p-comfy-xl">
            <ButtonStyle variant="primary">
              <Link href={`/sponsor-a-child?${sacFilters}`}>
                {t('core.actions.sponsorAChild')}
              </Link>
            </ButtonStyle>
          </div>
        )}
      </div>
      {showChips && (
        // @TODO: handle "All" selection
        <ChipGroup
          chips={chips}
          selectedValues={sacFilters?.filters}
          handleSelectionChange={(v) => {
            setSacFilters?.({
              filters: v as SacFiltersEnum[],
            });
          }}
          isScrollable
        />
      )}
    </div>
  );
};

export const LiveSacControls: React.FC<SacControlsProps> = (props) => {
  const { sacFilters, setSacFilters } = useSacQueryParams();
  return (
    <InternalSacControls
      {...props}
      sacFilters={sacFilters}
      setSacFilters={setSacFilters}
    />
  );
};

// During SSG we render the controls without caring about search params,
// so we can deliver the block quickly. Then, when Suspense finishes,
// we update it with the search params on the client.
export const SacControls: React.FC<SacControlsProps> = (props) => {
  return (
    <Suspense fallback={<InternalSacControls {...props} />}>
      <LiveSacControls {...props} />
    </Suspense>
  );
};
